'use client';
import localisePath from '@app/lib/helpers/localise-path';
import {
    breakpointExtraSmall,
    breakpointLarge,
    breakpointMedium,
    breakpointSmall,
} from 'config/styles/breakpoints';
import { brandWhite } from 'config/styles/colours';
import { LinkableWrapper } from 'helpers/linkableWrapper';
import PropTypes from 'prop-types';
import css from 'styled-jsx/css';
import SSRLink from '../PrismicComponents/SSRLink';

const MonumentTilesItem = ({ block, items, context }) => {
    const {
        layout,
        heading,
        body_text,
        font_family,
        font_colour,
        variant_type,
        content_size,
        mobile_image,
        desktop_image,
        background_colour,
        content_position,
        tile_space_between,
    } = block;

    const splitCharacter = '^';
    const contentSplitPattern = new RegExp(/\^/i, 'ig');
    const headingMode =
        heading?.indexOf(splitCharacter) === -1 ? 'automatic' : 'manual';
    const contentMode =
        body_text?.indexOf(splitCharacter) === -1 ? 'automatic' : 'manual';
    const formattedHeading = heading?.replace(contentSplitPattern, '\n');
    const formattedBodyText = body_text?.replace(contentSplitPattern, '\n');
    const formattedLayoutName = layout?.replace(/\s+/g, '-').toLowerCase();
    const hasImages = !!desktop_image?.url && mobile_image?.url;
    const formattedFontFamily = font_family?.replace(/\s+/g, '-').split('_')[0];
    const fontWeightFromFamily = font_family
        ?.replace(/\s+/g, '-')
        .split('_')[1];
    const fontWeight = fontWeightFromFamily ? fontWeightFromFamily : 'Light';
    const variantOrderType = (variantType) =>
        variantType?.toLowerCase().replace(/ /g, '_');
    const repeatableCTA = items.map((item) => item);
    const firstCTALink = repeatableCTA?.[0]?.call_to_action_link;
    const applyLinkToWholeTileCondition = !!(
        firstCTALink && repeatableCTA?.length <= 1
    );
    const firstCTAExternalTab = repeatableCTA?.[0]?.external_tab;

    const spanLinkStyles = css.resolve`
        span {
            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
            ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
            color: ${font_colour ? font_colour : brandWhite};
            white-space: nowrap;
            line-height: 1.5em;
            display: inline-flex;
            text-underline-offset: 4px;
            text-decoration-thickness: 1px;
            text-decoration: underline;
            cursor: pointer;
        }
    `;

    const linkStyles = css.resolve`
        a {
            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
            ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
            color: ${font_colour ? font_colour : brandWhite};
            white-space: nowrap;
            line-height: 1.5em;
            display: inline-flex;
            text-underline-offset: 4px;
            text-decoration-thickness: 1px;
        }
    `;

    const linkableWrapper = css.resolve`
        a {
            display: block;
        }
    `;

    const bgColor = !!background_colour;

    const contentShouldOverlay = (hasImage, hasBGColour, isStacked) => {
        if (isStacked) return false;
        return !!(hasImage || hasBGColour);
    };
    return (
        <>
            <div
                className={`monumentTiles_element ${variantOrderType(
                    variant_type
                )}`}
            >
                <LinkableWrapper
                    applyLink={applyLinkToWholeTileCondition}
                    linkUrl={localisePath(firstCTALink, context.lang)}
                    className={linkableWrapper.className}
                    externalTab={firstCTAExternalTab}
                >
                    {desktop_image?.url && (
                        <img
                            src={desktop_image.url}
                            alt={
                                desktop_image?.alt ||
                                heading ||
                                'Monument tile image'
                            }
                            className="desktop_only"
                        />
                    )}
                    {mobile_image?.url && (
                        <img
                            src={mobile_image?.url}
                            alt={
                                mobile_image?.alt ||
                                heading ||
                                'Monument tile image'
                            }
                            className="mobile_only"
                        />
                    )}
                    <div
                        className={`${
                            contentShouldOverlay(
                                hasImages,
                                bgColor,
                                content_position
                            )
                                ? 'overlayTiles_content'
                                : 'stacked_content'
                        } contentSize${content_size} ${formattedLayoutName} ${formattedFontFamily}`}
                    >
                        <div className="content">
                            {formattedHeading && (
                                <h2 className="heading">{formattedHeading}</h2>
                            )}
                            {formattedBodyText && (
                                <div className="body_text">
                                    {formattedBodyText}
                                </div>
                            )}
                            {items.map((item, index) => {
                                return (
                                    item?.call_to_action && (
                                        <SSRLink
                                            asSpan={
                                                applyLinkToWholeTileCondition
                                                    ? true
                                                    : false
                                            }
                                            linkType={
                                                'collections' || 'products'
                                            }
                                            linkUrl={localisePath(
                                                item?.call_to_action_link,
                                                context.lang
                                            )}
                                            className={
                                                applyLinkToWholeTileCondition
                                                    ? spanLinkStyles.className
                                                    : linkStyles.className
                                            }
                                            gtm_idƒentifier={
                                                item?.analytics_event_description
                                            }
                                            title={item?.call_to_action}
                                            externalTab={item?.external_tab}
                                            key={index}
                                        >
                                            {item?.call_to_action}
                                        </SSRLink>
                                    )
                                );
                            })}
                        </div>
                    </div>
                </LinkableWrapper>
            </div>
            {linkStyles.styles}
            <style jsx>
                {`
                    .monumentTiles_element {
                        flex: 1;
                        position: relative;
                        ${background_colour
                            ? `background-color: ${background_colour}`
                            : ''};
                        font-size: 16px; /* Reset the base font-size */
                        ${tile_space_between ? `margin: 10px` : ''};
                        box-sizing: border-box;
                    }

                    .overlayTiles_content {
                        display: flex;
                        ${hasImages ? `position: absolute;` : ''};
                        height: 100%;
                        width: 100%;
                        ${hasImages ? `top: 0;` : ''};
                        box-sizing: border-box;
                        ${background_colour
                            ? `background-color: ${background_colour}`
                            : ''};
                    }

                    .stacked_content {
                        display: flex;
                        box-sizing: border-box;
                        ${contentShouldOverlay(
                            hasImages,
                            bgColor,
                            content_position
                        )
                            ? ''
                            : `padding: 20px;`};
                    }

                    .content {
                        color: ${font_colour ? font_colour : brandWhite};
                        word-break: normal;
                    }

                    .heading {
                        font-weight: bold;
                        margin: 0 auto;
                        line-height: 1em;
                        ${headingMode === 'manual'
                            ? `white-space: pre-wrap`
                            : ''}
                    }

                    // Defines the layout positioning
                    .overlayTiles_content.left-top {
                        align-items: flex-start;
                        padding: 55px;
                    }

                    .overlayTiles_content.left-bottom {
                        align-items: flex-end;
                        padding: 55px;
                    }

                    .overlayTiles_content.left-middle {
                        align-items: center;
                        padding: 55px;
                    }

                    .overlayTiles_content.center {
                        align-items: center;
                        justify-content: center;
                        text-align: center;
                        padding: 55px;
                    }

                    /* Acumin Font Sizes for Mobile */
                    .contentSizeSmall.Acumin-Pro .heading {
                        font-size: 2.8125em;
                        ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                        ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
                        ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                    }

                    .contentSizeMedium.Acumin-Pro .heading {
                        font-size: 3.4375em;
                        ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                        ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
                        ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                    }

                    .contentSizeLarge.Acumin-Pro .heading {
                        font-size: 3.75em;
                        ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                        ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
                        ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                    }

                    .contentSizeSmall.Acumin-Pro .body_text {
                        font-size: 0.9375em;
                        ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                        ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
                        ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                    }

                    .contentSizeMedium.Acumin-Pro .body_text {
                        font-size: 1.125em;
                        ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                        ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
                        ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                    }

                    .contentSizeLarge.Acumin-Pro .body_text {
                        font-size: 1.375em;
                        ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                        ${fontWeight === 'Medium' ? `font-weight: 500` : ''};
                        ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                    }

                    @media (min-width: 1260px) {
                        /* Acumin Font Sizes for Desktop */
                        .contentSizeSmall.Acumin-Pro .heading {
                            font-size: 4.7em;
                            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                            ${fontWeight === 'Medium'
                                ? `font-weight: 500`
                                : ''};
                            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                        }

                        .contentSizeMedium.Acumin-Pro .heading {
                            font-size: 5.625em;
                            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                            ${fontWeight === 'Medium'
                                ? `font-weight: 500`
                                : ''};
                            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                        }

                        .contentSizeLarge.Acumin-Pro .heading {
                            font-size: 6.875em;
                            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                            ${fontWeight === 'Medium'
                                ? `font-weight: 500`
                                : ''};
                            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                        }

                        .contentSizeSmall.Acumin-Pro .body_text {
                            font-size: 1.125em;
                            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                            ${fontWeight === 'Medium'
                                ? `font-weight: 500`
                                : ''};
                            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                        }

                        .contentSizeMedium.Acumin-Pro .body_text {
                            font-size: 1.375em;
                            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                            ${fontWeight === 'Medium'
                                ? `font-weight: 500`
                                : ''};
                            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                        }

                        .contentSizeLarge.Acumin-Pro .body_text {
                            font-size: 1.625em;
                            ${fontWeight === 'Light' ? `font-weight: 300` : ''};
                            ${fontWeight === 'Medium'
                                ? `font-weight: 500`
                                : ''};
                            ${fontWeight === 'Black' ? `font-weight: 800` : ''};
                        }
                    }

                    /* Monument Font sizes for mobile */
                    .contentSizeSmall.MonumentExtended-Black .heading,
                    .contentSizeSmall.MonumentExtended-Regular .heading {
                        font-size: 1.875em;
                    }

                    .contentSizeMedium.MonumentExtended-Black .heading,
                    .contentSizeMedium.MonumentExtended-Regular .heading {
                        font-size: 2.5em;
                    }

                    .contentSizeLarge.MonumentExtended-Black .heading,
                    .contentSizeLarge.MonumentExtended-Regular .heading {
                        font-size: 3.125em;
                    }

                    .contentSizeSmall.MonumentExtended-Black .body_text,
                    .contentSizeSmall.MonumentExtended-Regular .body_text {
                        font-size: 0.95em;
                    }

                    .contentSizeMedium.MonumentExtended-Black .body_text,
                    .contentSizeMedium.MonumentExtended-Regular .body_text {
                        font-size: 1.125em;
                    }

                    .contentSizeLarge.MonumentExtended-Black .body_text,
                    .contentSizeLarge.MonumentExtended-Regular .body_text {
                        font-size: 1.375em;
                    }

                    @media (min-width: 1260px) {
                        /* Monument Font sizes for desktop */
                        .contentSizeSmall.MonumentExtended-Black .heading,
                        .contentSizeSmall.MonumentExtended-Regular .heading {
                            font-size: 2.625em;
                        }

                        .contentSizeMedium.MonumentExtended-Black .heading,
                        .contentSizeMedium.MonumentExtended-Regular .heading {
                            font-size: 3.625em;
                        }

                        .contentSizeLarge.MonumentExtended-Black .heading,
                        .contentSizeLarge.MonumentExtended-Regular .heading {
                            font-size: 5.625em;
                        }

                        .contentSizeSmall.MonumentExtended-Black .body_text,
                        .contentSizeSmall.MonumentExtended-Regular .body_text {
                            font-size: 1.125em;
                        }

                        .contentSizeMedium.MonumentExtended-Black .body_text,
                        .contentSizeMedium.MonumentExtended-Regular .body_text {
                            font-size: 1.375em;
                        }

                        .contentSizeLarge.MonumentExtended-Black .body_text,
                        .contentSizeLarge.MonumentExtended-Regular .body_text {
                            font-size: 1.625em;
                        }
                    }

                    .heading {
                        font-weight: 600;
                        ${font_family
                            ? `font-family: ${formattedFontFamily}, sans-serif;`
                            : ''}
                    }

                    .body_text {
                        font-weight: 300;
                        ${contentMode === 'manual'
                            ? `white-space: pre-wrap`
                            : ''}
                    }

                    .contentSizeSmall .content h2,
                    .contentSizeSmall .content h1 {
                        margin-bottom: 2px;
                        ${formattedFontFamily === 'Acumin-Pro'
                            ? `margin-bottom: 10px;`
                            : 'margin-bottom: 8px;'}
                    }

                    .contentSizeSmall .content div {
                        line-height: 1.5em;
                        margin: 8px 0;
                    }

                    .contentSizeMedium .content h2,
                    .contentSizeMedium .content h1 {
                        ${formattedFontFamily === 'Acumin-Pro'
                            ? `margin-bottom: 10px;`
                            : 'margin-bottom: 8px;'}
                    }

                    .contentSizeMedium .content div {
                        line-height: 1.5em;
                        margin: 8px 0;
                    }

                    .contentSizeLarge .content h2,
                    .contentSizeLarge .content h1 {
                        ${formattedFontFamily === 'Acumin-Pro'
                            ? `margin-bottom: 10px;`
                            : 'margin-bottom: 8px;'}
                    }

                    .contentSizeLarge .content div {
                        line-height: 1.5em;
                        margin: 8px 0;
                    }

                    @media (min-width: ${breakpointMedium}) {
                        .mobile_only {
                            display: none;
                        }

                        .desktop_only {
                            width: 100%;
                            display: block;
                        }
                    }

                    @media (max-width: ${breakpointMedium}) {
                        .mobile_only {
                            width: 100%;
                            display: block;
                        }

                        .desktop_only {
                            display: none;
                        }

                        .overlayTiles_content.left-top {
                            padding: 35px;
                        }

                        .overlayTiles_content.left-bottom {
                            padding: 35px;
                        }

                        .overlayTiles_content.left-middle {
                            padding: 35px;
                        }

                        .overlayTiles_content.center {
                            padding: 35px;
                        }
                    }

                    @media (max-width: ${breakpointSmall}) {
                        .body_text {
                            line-height: 1.6em;
                        }

                        .heading {
                            font-weight: 600;
                        }
                    }

                    @media (max-width: ${breakpointExtraSmall}) {
                        .overlayTiles_content.left-top,
                        .overlayTiles_content.left-middle,
                        .overlayTiles_content.left-bottom {
                            padding-left: 20px;
                        }

                        .contentSizeLarge.MonumentExtended-Black .heading,
                        .contentSizeLarge.MonumentExtended-Regular .heading {
                            font-size: 2.5em;
                        }

                        .contentSizeMedium.MonumentExtended-Black .heading,
                        .contentSizeMedium.MonumentExtended-Regular .heading {
                            font-size: 2.1em;
                        }

                        .contentSizeLarge.Acumin-Pro .heading {
                            font-size: 3em;
                        }
                    }
                `}
            </style>
            <style jsx global>
                {`
                    .heading > p {
                        margin: 0;
                    }

                    .stacked_content.center a {
                        padding: 5px 15px 5px 15px;
                        justify-content: center;
                    }

                    .stacked_content.left-top,
                    .left-middle,
                    .left-bottom a {
                        padding: 5px 30px 5px 0;
                    }

                    /* Monument Font sizes for mobile */
                    .contentSizeLarge.MonumentExtended-Black a,
                    .contentSizeLarge.MonumentExtended-Regular a {
                        font-size: 1.375em;
                    }

                    .contentSizeMedium.MonumentExtended-Black a,
                    .contentSizeMedium.MonumentExtended-Regular a {
                        font-size: 1.125em;
                    }

                    .contentSizeSmall.MonumentExtended-Black a,
                    .contentSizeSmall.MonumentExtended-Regular a {
                        font-size: 0.95em;
                    }

                    /* Acumin Font Sizes for Mobile */
                    .contentSizeSmall.Acumin-Pro a {
                        font-size: 0.9375em;
                    }

                    .contentSizeMedium.Acumin-Pro a {
                        font-size: 1.125em;
                    }

                    .contentSizeLarge.Acumin-Pro a {
                        font-size: 1.375em;
                    }

                    @media (min-width: ${breakpointMedium}) {
                        /* Monument Font sizes for desktop */
                        .contentSizeLarge.MonumentExtended-Black a,
                        .contentSizeLarge.MonumentExtended-Regular a {
                            font-size: 1.625em;
                        }

                        .contentSizeMedium.MonumentExtended-Black a,
                        .contentSizeMedium.MonumentExtended-Regular a {
                            font-size: 1.375em;
                        }

                        .contentSizeSmall.MonumentExtended-Black a,
                        .contentSizeSmall.MonumentExtended-Regular a {
                            font-size: 1.125em;
                        }

                        /* Acumin Font Sizes for Desktop */
                        .contentSizeSmall.Acumin-Pro a {
                            font-size: 1.125em;
                        }

                        .contentSizeMedium.Acumin-Pro a {
                            font-size: 1.375em;
                        }

                        .contentSizeLarge.Acumin-Pro a {
                            font-size: 1.625em;
                        }
                    }
                `}
            </style>
        </>
    );
};

const RowWidthFiller = (block) => {
    const sliceSize = block?.slice_size;
    if (sliceSize === 'Half Width') {
        return 1;
    } else if (sliceSize === 'Third Width') {
        return 2;
    } else if (sliceSize === 'Quarter Width') {
        return 3;
    } else {
        return 1;
    }
};

const MonumentTiles = ({ block, items, context }) => {
    if (!block || !items) return null;

    const fillRow = RowWidthFiller(block);

    return (
        <>
            <div className="monumentBlock" data-siblings-tiles-count={fillRow}>
                <MonumentTilesItem
                    block={block}
                    items={items}
                    siblingsCount={fillRow}
                    context={context}
                />
            </div>
            <style jsx global>
                {`
                    [data-siblings-tiles-count='1'] .content {
                        width: 50%;
                    }

                    [data-siblings-tiles-count='2'] .content {
                        width: 100%;
                    }

                    [data-siblings-tiles-count='3'] .content {
                        width: 100%;
                    }

                    [data-siblings-tiles-count='1'] {
                        flex: 1 0 50%;
                    }

                    [data-siblings-tiles-count='2'] {
                        display: flex;
                        flex-direction: row;
                        width: 33.3%;
                    }

                    [data-siblings-tiles-count='3'] {
                        display: flex;
                        flex-direction: row;
                        width: 25%;
                    }

                    @media (max-width: ${breakpointLarge}) {
                        [data-siblings-tiles-count='1'] .content {
                            width: 100%;
                        }

                        [data-siblings-tiles-count='2'] .content {
                            width: 100%;
                        }

                        [data-siblings-tiles-count='3'] .content {
                            width: 100%;
                        }
                    }

                    @media (max-width: ${breakpointSmall}) {
                        [data-siblings-tiles-count='1'] {
                            flex: 1 0 100%;
                        }

                        [data-siblings-tiles-count='1'] .content {
                            width: 100%;
                        }

                        [data-siblings-tiles-count='2'] {
                            flex: 1 0 100%;
                        }

                        [data-siblings-tiles-count='2'] .content {
                            width: 100%;
                        }

                        [data-siblings-tiles-count='3'] {
                            flex: 1 0 50%;
                        }

                        [data-siblings-tiles-count='3'] .content {
                            width: 100%;
                        }
                    }

                    @media (max-width: ${breakpointMedium}) {
                        [data-siblings-tiles-count='1'] {
                            flex: 1 0 100%;
                        }

                        [data-siblings-tiles-count='2'] {
                            flex: 1 0 100%;
                        }

                        [data-siblings-tiles-count='3'] {
                            flex: 1 0 50%;
                        }
                    }
                `}
            </style>
            <style jsx>
                {`
                    .monumentBlock {
                        display: flex;
                    }

                    @media (max-width: ${breakpointMedium}) {
                        .monumentBlock {
                            flex-direction: column;
                        }
                    }
                `}
            </style>
        </>
    );
};

MonumentTiles.propTypes = {
    block: PropTypes.object,
};

MonumentTilesItem.propTypes = {
    block: PropTypes.object.isRequired,
};

export default MonumentTiles;
